import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';
import { init as initSentry } from '@sentry/angular';
import { ConfigService } from './app/core/services/config.service';
import { installInsecureHistoryGuard, isInsecureHistoryError } from './app/shared/browser/insecure-history';
import { isExpectedPromptResponseStoryDeleteError } from './app/shared/graphql/expected-story-delete-errors';
import { isUploadNetworkErrorMessage } from './app/shared/http/upload-network-error';
import { isUnownedFailedFetch } from './app/shared/sentry/unowned-failed-fetch';

if (environment.production) {
  enableProdMode();
}

// Auth0 SPA SDK (and therefore @auth0/auth0-angular) requires a secure
// origin. If a user reaches the production app over plain HTTP, redirect
// them to HTTPS before Angular/Auth0 bootstraps. This prevents the
// auth0-spa-js "must run on a secure origin" error (SLAPFIVE-FRONTEND-A3W).
// Local development over http://localhost is still allowed by the SDK.
function redirectToHttpsIfNeeded(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  const isLocalHttp =
    window.location.protocol === 'http:' &&
    /^localhost$|^127\.0\.0\.1$|^\[::1\]$/i.test(host);

  if (window.location.protocol !== 'https:' && !isLocalHttp) {
    window.location.replace(window.location.href.replace(/^http:/, 'https:'));
    return true;
  }
  return false;
}

// Initialize config and Sentry BEFORE bootstrapping Angular
// This ensures ConfigService has config available when DI creates providers
async function bootstrap() {
  // Install before Sentry instruments history, so Firefox's sandboxed-iframe
  // SecurityError never rejects the board-embed navigation (#15650).
  installInsecureHistoryGuard();
  try {
    // Fetch the app config before Angular bootstrap
    // This config is used for both Sentry init and Angular's ConfigService
    const response = await fetch(environment.configUrl);
    const config = await response.json();

    // Pre-load config into ConfigService to avoid race condition
    // where DI tries to use config before APP_INITIALIZER completes
    ConfigService.setPreloadedConfig(config);

    if (config.sentryDsn) {
      const isLocal = config.environmentName === 'local' || config.environmentName === 'local-lan';
      const sentryEnabled = localStorage.getItem('SENTRY_ENABLED') === 'true';

      // In local environment, only init Sentry if explicitly enabled via localStorage
      // In all other environments, always init Sentry
      if (!isLocal || sentryEnabled) {
        initSentry({
          dsn: config.sentryDsn,
          environment: config.environmentName,
          beforeSend(event) {
            // Filter out errors from bots with monkey-patched DOM that break Angular's RouterLink
            // These bots override addEventListener to immediately invoke handlers with undefined events
            // See: https://github.com/slapfive/slapfive/issues/9147
            const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
            const hasRouterLinkFrame = frames.some(
              (frame) => frame.function?.includes('RouterLink_click_HostBindingHandler')
            );
            if (
              errorMessage.includes("Cannot read properties of undefined (reading 'button')") &&
              hasRouterLinkFrame
            ) {
              return null;
            }

            // Transient Hasura/Postgres "connection error" on GraphQL subscriptions
            // (SLAPFIVE-FRONTEND-A2S / A2T). Already retried at the transport layer.
            if (/^(GraphQL error: |ApolloError: )?connection error$/i.test(errorMessage.trim())) {
              return null;
            }

            // Story delete blocked by PromptResponse.story_id RESTRICT (#10848).
            // The UI toasts this; it is not an application fault (#16355).
            if (isExpectedPromptResponseStoryDeleteError(errorMessage)) {
              return null;
            }

            // Survey file PUT dropped by the browser. The member is asked to
            // retry; this is not an application fault (#16364 / A5D).
            if (isUploadNetworkErrorMessage(errorMessage)) {
              return null;
            }

            // Filter out WalkMe errors (no stacktrace, identified via serialized extra data)
            const serialized = (event.extra as Record<string, unknown>)?.__serialized__ as Record<string, unknown> | undefined;
            const extraData = serialized?.extraData as Record<string, unknown> | undefined;
            if (typeof extraData?.file === 'string' && extraData.file.includes('walkme.com')) {
              return null;
            }

            // History API SecurityErrors in embedded boards. Chrome reports a
            // cross-origin replaceState (#9324); Firefox reports
            // "The operation is insecure" inside a sandboxed iframe (#15650).
            if (isInsecureHistoryError(errorMessage)) {
              return null;
            }

            // Auth0 popup/redirect state mismatch on embed login (SLAPFIVE-FRONTEND-A2Z).
            // Expected when a previous iframe popup race leaves stale transaction state.
            if (
              /^invalid state$/i.test(errorMessage.trim()) ||
              errorMessage.toLowerCase().includes('state_mismatch')
            ) {
              return null;
            }

            // "Failed to fetch" from an inline page script, with no application
            // frame (SLAPFIVE-FRONTEND-A62). A fetch failure in our code or the
            // Auth0 SDK still has a named function and is reported.
            if (isUnownedFailedFetch(errorMessage, frames)) {
              return null;
            }

            // Auth0 SPA SDK requires a secure origin. We redirect HTTP to HTTPS
            // before bootstrap, but filter any remaining occurrences to avoid noise.
            // See SLAPFIVE-FRONTEND-A3W.
            if (errorMessage.toLowerCase().includes('must run on a secure origin')) {
              return null;
            }

            // Filter out errors originating from third-party scripts (e.g., Salesforce plugins)
            // These errors bubble through Zone.js (polyfills.js) but originate from external code
            // We check if the top frame (where error was thrown) is from a known third-party pattern
            const thirdPartyPatterns = [
              /\/users\/[^/]+\/scripts\/prelib-plugin-/, // Salesforce Lightning plugin scripts
              /^chrome-extension:\/\//, // Browser extensions
              /\/gtm\.js/, // Google Tag Manager
              /\/analytics\.js/, // Google Analytics
            ];
            const topFrame = frames[frames.length - 1]; // Sentry frames are ordered bottom-to-top
            if (topFrame?.filename) {
              const isThirdParty = thirdPartyPatterns.some((pattern) => pattern.test(topFrame.filename));
              if (isThirdParty) {
                return null;
              }
            }

            return event;
          },
        });
      }
    }
  } catch (err) {
    console.warn('Failed to load app config:', err);
  }

  // Now bootstrap Angular with config already pre-loaded
  bootstrapApplication(AppComponent, appConfig)
    .catch(err => console.error(err));
}

if (!redirectToHttpsIfNeeded()) {
  bootstrap();
}
